import React, { useEffect, useMemo, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import api from '../../services/api';
import { CrmButton, CrmModal, CrmSearchInput, CrmSelect } from '../ui';
import './media-asset-library.scss';

export const mediaUrl = (value) => {
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  const base = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
  try { return new URL(value, new URL(base, window.location.origin).origin).href; } catch { return value; }
};

export const mediaLabel = (asset) => asset?.alt_text || asset?.caption || asset?.original_filename || asset?.filename || `Asset ${asset?.id || ''}`;

export default function MediaAssetLibrary({
  mediaType = '',
  multiple = false,
  initialIds = [],
  selectedIds,
  onSelectionChange,
  onSelect,
  showSelectionAction = true,
  manage = false,
  storefrontId,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState(mediaType);
  const [selected, setSelected] = useState(() => new Map((initialIds || []).map((id) => [String(id), { id }])));
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [preview, setPreview] = useState(null); // asset opened from the Media page
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  // When Previous/Next crosses a page boundary, open the first/last asset once the new page has loaded.
  const pendingPreviewRef = useRef(null);

  const copyLink = async (asset) => {
    try {
      await navigator.clipboard.writeText(mediaUrl(asset.url));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy the link.');
    }
  };

  const controlledKey = useMemo(() => Array.isArray(selectedIds) ? selectedIds.map(String).sort().join('|') : '', [selectedIds]);

  useEffect(() => {
    if (!Array.isArray(selectedIds)) return;
    setSelected((current) => {
      const next = new Map();
      selectedIds.forEach((id) => {
        const key = String(id);
        next.set(key, current.get(key) || items.find((asset) => String(asset.id) === key) || { id });
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/media-assets', {
          params: { page, page_size: 24, search, media_type: type || undefined },
        });
        if (!active) return;
        const nextItems = data.items || [];
        setItems(nextItems);
        setTotal(data.total || 0);
        const pending = pendingPreviewRef.current;
        if (pending && nextItems.length) {
          if (pending === 'last') setPreview(nextItems[nextItems.length - 1]);
          else if (pending === 'first') setPreview(nextItems[0]);
          else if (typeof pending.index === 'number') setPreview(nextItems[Math.min(pending.index, nextItems.length - 1)]);
        } else if (pending && typeof pending.index === 'number') {
          setPreview(null); // nothing left to show
        }
        pendingPreviewRef.current = null;
        setError('');
        setSelected((old) => {
          const next = new Map(old);
          nextItems.forEach((asset) => {
            if (next.has(String(asset.id))) next.set(String(asset.id), asset);
          });
          return next;
        });
      } catch (e) {
        if (active) setError(e.response?.data?.error || 'Could not load media.');
      } finally {
        if (active) setLoading(false);
      }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [page, search, type, revision]);

  const publishSelection = (next) => {
    setSelected(next);
    onSelectionChange?.([...next.values()]);
  };

  const choose = (asset) => {
    const key = String(asset.id);
    const next = multiple ? new Map(selected) : new Map();
    if (multiple && next.has(key)) next.delete(key);
    else next.set(key, asset);
    publishSelection(next);
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      let next = multiple ? new Map(selected) : new Map();
      for (const file of files) {
        if (type && !file.type.startsWith(`${type.toLowerCase()}/`)) throw new Error(`Choose a ${type.toLowerCase()} file.`);
        const body = new FormData();
        body.append('file', file);
        if (storefrontId) body.append('storefront_id', storefrontId);
        const { data } = await api.post('/media-assets/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (!manage) {
          if (!multiple) next = new Map();
          next.set(String(data.id), data);
        }
      }
      if (!manage) publishSelection(next);
      setPage(1);
      setSearch('');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setRevision((n) => n + 1);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/media-assets/${deleting.id}`);
      const next = new Map(selected);
      next.delete(String(deleting.id));
      publishSelection(next);
      const deletedIndex = items.findIndex((asset) => String(asset.id) === String(deleting.id));
      const deletedFromPreview = preview && String(preview.id) === String(deleting.id);
      setDeleting(null);
      if (deletedFromPreview) {
        // Stay on this page and show the asset that takes the deleted one's place.
        if (items.length <= 1 && page > 1) {
          pendingPreviewRef.current = 'last';
          setPage((p) => Math.max(1, p - 1));
        } else {
          pendingPreviewRef.current = { index: Math.max(0, deletedIndex) };
        }
      } else {
        setPage(1);
      }
      setRevision((n) => n + 1);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not delete media.');
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const inspectUsage = async (asset) => {
    try {
      const { data } = await api.get(`/media-assets/${asset.id}/usage`);
      setUsage({ asset, ...data });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load usage.');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 24));
  const start = total ? ((page - 1) * 24) + 1 : 0;
  const end = Math.min(page * 24, total);

  // Previous/Next inside the asset preview; crosses into the neighbouring page when needed.
  const previewIndex = preview ? items.findIndex((asset) => String(asset.id) === String(preview.id)) : -1;
  const previewPosition = previewIndex >= 0 ? ((page - 1) * 24) + previewIndex + 1 : 0;
  const hasPrevious = previewIndex > 0 || page > 1;
  const hasNext = (previewIndex >= 0 && previewIndex < items.length - 1) || page < totalPages;
  const showPrevious = () => {
    if (previewIndex > 0) setPreview(items[previewIndex - 1]);
    else if (page > 1) { pendingPreviewRef.current = 'last'; setPage((p) => p - 1); }
  };
  const showNext = () => {
    if (previewIndex >= 0 && previewIndex < items.length - 1) setPreview(items[previewIndex + 1]);
    else if (page < totalPages) { pendingPreviewRef.current = 'first'; setPage((p) => p + 1); }
  };
  const previewKeysRef = useRef({});
  previewKeysRef.current = { showPrevious, showNext, hasPrevious, hasNext, blocked: Boolean(deleting || usage) };
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (event) => {
      const keys = previewKeysRef.current;
      if (keys.blocked || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target?.tagName || '')) return;
      if (event.key === 'ArrowLeft' && keys.hasPrevious) { event.preventDefault(); keys.showPrevious(); }
      if (event.key === 'ArrowRight' && keys.hasNext) { event.preventDefault(); keys.showNext(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [preview]);

  return (
    <div className="media-library">
      <div className="media-library__toolbar">
        <CrmSearchInput
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search media"
          aria-label="Search media"
        />
        {!mediaType ? (
          <CrmSelect value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} aria-label="Media type">
            <option value="">All assets</option>
            <option value="IMAGE">Images</option>
            <option value="VIDEO">Videos</option>
          </CrmSelect>
        ) : null}
        <CrmButton variant="secondary" startIcon={<RefreshIcon fontSize="small" />} disabled={loading} onClick={() => setRevision((n) => n + 1)}>
          Refresh
        </CrmButton>
        <CrmButton variant="primary" startIcon={<UploadFileIcon fontSize="small" />} loading={uploading} onClick={() => fileRef.current?.click()}>
          Upload new
        </CrmButton>
        <input ref={fileRef} hidden type="file" multiple={manage || multiple} accept={type ? `${type.toLowerCase()}/*` : 'image/*,video/*'} onChange={upload} />
      </div>

      {error ? <div className="media-library__message media-library__message--error">{error}</div> : null}

      {loading ? (
        <div className="media-library__loading"><span className="crm-button__spinner" /> Loading media…</div>
      ) : items.length ? (
        <div className="media-library__grid">
          {items.map((asset) => {
            const isSelected = selected.has(String(asset.id));
            return (
              <div key={asset.id} className={`media-library__asset ${isSelected ? 'is-selected' : ''}`}>
                <button
                  type="button"
                  className="media-library__asset-button"
                  disabled={!manage && !['IMAGE', 'VIDEO'].includes(asset.media_type)}
                  onClick={() => (manage ? setPreview(asset) : choose(asset))}
                  aria-label={`${manage ? 'Open' : 'Select'} ${mediaLabel(asset)}`}
                  aria-pressed={isSelected}
                >
                  <div className="media-library__preview">
                    {asset.media_type === 'VIDEO' ? (
                      <video src={mediaUrl(asset.url)} muted preload="metadata" />
                    ) : asset.media_type === 'IMAGE' ? (
                      <img src={mediaUrl(asset.url)} alt={asset.alt_text || ''} loading="lazy" />
                    ) : (
                      <span className="media-library__document">Document</span>
                    )}
                    {!manage && isSelected ? <span className="media-library__selected"><CheckCircleIcon fontSize="small" /></span> : null}
                  </div>
                  <div className="media-library__asset-name" title={mediaLabel(asset)}>{mediaLabel(asset)}</div>
                </button>
                {manage ? (
                  <div className="media-library__asset-actions">
                    <CrmButton size="small" variant="tertiary" startIcon={<InfoOutlinedIcon fontSize="small" />} onClick={() => inspectUsage(asset)}>Usage</CrmButton>
                    <CrmButton size="small" variant="tertiary" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setDeleting(asset)}>Delete</CrmButton>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="media-library__message">No media found. Upload an asset or change your search.</div>
      )}

      <div className="media-library__pagination">
        <span>{total ? `Showing ${start}–${end} of ${total}` : '0 assets'}</span>
        <div>
          <CrmButton iconOnly size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page"><ChevronLeftIcon fontSize="small" /></CrmButton>
          <span className="media-library__page">{page} / {totalPages}</span>
          <CrmButton iconOnly size="small" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page"><ChevronRightIcon fontSize="small" /></CrmButton>
        </div>
      </div>

      {!manage && showSelectionAction ? (
        <div className="media-library__inline-action">
          <CrmButton variant="primary" disabled={uploading || !selected.size || [...selected.values()].some((asset) => !asset.url)} onClick={() => onSelect?.([...selected.values()])}>
            {multiple ? `Use selected (${selected.size})` : 'Use this'}
          </CrmButton>
        </div>
      ) : null}

      <CrmModal
        open={Boolean(deleting)}
        onClose={() => !busy && setDeleting(null)}
        title="Delete media?"
        size="sm"
        footer={<CrmButton variant="danger" loading={busy} onClick={remove}>Delete</CrmButton>}
      >
        <p>{deleting ? mediaLabel(deleting) : ''}</p>
        <p className="media-library__muted">Media that is still in use cannot be deleted.</p>
      </CrmModal>

      <CrmModal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={preview ? mediaLabel(preview) : ''}
        subtitle={preview ? [preview.media_type, preview.mime_type, preview.original_filename].filter(Boolean).join(' · ') : ''}
        size="lg"
        className="media-library__preview-modal"
        footer={preview ? (
          <div className="media-library__preview-actions">
            <div className="media-library__preview-nav">
              <CrmButton variant="secondary" startIcon={<ChevronLeftIcon fontSize="small" />} disabled={!hasPrevious || loading} onClick={showPrevious}>Previous</CrmButton>
              <span className="media-library__muted">{previewPosition ? `${previewPosition} of ${total}` : ''}</span>
              <CrmButton variant="secondary" endIcon={<ChevronRightIcon fontSize="small" />} disabled={!hasNext || loading} onClick={showNext}>Next</CrmButton>
            </div>
            <div className="media-library__preview-buttons">
              <CrmButton variant="tertiary" startIcon={<InfoOutlinedIcon fontSize="small" />} onClick={() => inspectUsage(preview)}>Usage</CrmButton>
              <CrmButton variant="tertiary" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setDeleting(preview)}>Delete</CrmButton>
              <CrmButton variant="secondary" onClick={() => copyLink(preview)}>{copied ? 'Link copied' : 'Copy link'}</CrmButton>
              <a className="crm-button crm-button--primary crm-button--default" href={mediaUrl(preview.url)} target="_blank" rel="noopener noreferrer">Open in new tab</a>
            </div>
          </div>
        ) : null}
      >
        {preview && error ? <div className="media-library__message media-library__message--error" style={{ marginBottom: 12 }}>{error}</div> : null}
        {preview ? (
          <div className="media-library__preview-full">
            {preview.media_type === 'VIDEO' ? (
              <video src={mediaUrl(preview.url)} controls autoPlay muted />
            ) : preview.media_type === 'IMAGE' ? (
              <img src={mediaUrl(preview.url)} alt={preview.alt_text || mediaLabel(preview)} />
            ) : (
              <a href={mediaUrl(preview.url)} target="_blank" rel="noopener noreferrer">Open document</a>
            )}
          </div>
        ) : null}
      </CrmModal>

      <CrmModal
        open={Boolean(usage)}
        onClose={() => setUsage(null)}
        title="Media usage"
        size="sm"
        footer={<CrmButton variant="primary" onClick={() => setUsage(null)}>Done</CrmButton>}
      >
        <p><strong>{usage?.reference_count || 0}</strong> reference(s)</p>
        {(usage?.references || []).map((ref, index) => (
          <p key={`${ref.entity_type}-${ref.entity_id}-${index}`} className="media-library__muted">
            {ref.entity_type} #{ref.entity_id} — {ref.role}
          </p>
        ))}
      </CrmModal>
    </div>
  );
}
